import React, { useState, useEffect } from 'react';
import {
  IonBadge,
  IonButton,
  IonIcon,
  IonSkeletonText,
} from '@ionic/react';
import ProCard from './ProCard';
import {
  calendarOutline,
  chevronDownOutline,
  chevronUpOutline,
  alertCircleOutline,
  timeOutline,
  locationOutline,
} from 'ionicons/icons';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';

/**
 * Pro-namespaced calendar dashboard widget.
 *
 * Fork of the shared `components/mobile/CalendarCard` with lighter event row
 * backgrounds (`bg-background-secondary` + border) tuned for the Pro app. The shared
 * component keeps its `bg-background-quaternary` for the citizen experience.
 */


interface CalendarEventSummary {
  calendar_id: number;
  reading_title: string;
  calendar_description?: string;
  reading_date: string;
  reading_time?: string;
  room_number?: string;
  no_time: boolean;
  upon_adjournment: boolean;
  upon_adjournment_of_committee_hearing: boolean;
  chamber: 'Senate' | 'House' | string;
  joint_meeting: boolean;
  committee_flag: boolean;
  bill_numbers: string[];
  bills: Array<{ bill_id: string; bill_number: string; [key: string]: unknown }>;
  tracked_bills: Array<{ bill_id: string; bill_number: string; [key: string]: unknown }>;
}

interface CalendarWidgetData {
  upcomingEvents: CalendarEventSummary[];
  todayEvents: CalendarEventSummary[];
  totalUpcoming: number;
  nextEventDate?: string;
  summary: {
    total_events: number;
    senate_events: number;
    house_events: number;
    committee_events: number;
  };
}

export interface ProCalendarCardProps {
  queryKey?: string;
  refetchRef?: React.MutableRefObject<(() => Promise<any>) | null>;
  maxItems?: number;
}

const getChamberColor = (chamber: string) => {
  switch (chamber?.toLowerCase()) {
    case 'senate': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
    case 'house': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    default: return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
  }
};

const formatTime = (time?: string, uponAdjournment?: boolean) => {
  if (uponAdjournment) return 'Upon adjournment';
  if (time) {
    try {
      const d = new Date(`1970-01-01T${time}`);
      return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return time;
    }
  }
  return '';
};

const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateHeader = (dateStr: string): string => {
  const dateOnly = dateStr.split('T')[0];
  const date = new Date(dateOnly + 'T00:00:00');
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const todayStr = toLocalDateString(today);
  const tomorrowStr = toLocalDateString(tomorrow);

  if (dateOnly === todayStr) return 'Today';
  if (dateOnly === tomorrowStr) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const ProCalendarCard: React.FC<ProCalendarCardProps> = ({
  queryKey = 'pro-calendar',
  refetchRef,
  maxItems = 5,
}) => {
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<CalendarWidgetData>(
    [queryKey],
    async () => {
      const now = new Date();
      const params = new URLSearchParams({
        month: String(now.getMonth() + 1),
        year: String(now.getFullYear()),
        myEventsOnly: 'true',
      });
      const response = await api.get(`/dashboard/widgets/calendar/data?${params}`);
      return response.data?.data?.data || response.data?.data || response.data;
    },
    { staleTime: 900000, refetchInterval: 1800000 },
  );

  useEffect(() => {
    if (refetchRef) {
      refetchRef.current = refetch;
    }
  }, [refetchRef, refetch]);

  // Merge and deduplicate events, sorted by date then time
  const allEvents = React.useMemo(() => {
    if (!data) return [];
    const combined = [...(data.todayEvents || []), ...(data.upcomingEvents || [])];
    const seen = new Set<number>();
    const deduped: CalendarEventSummary[] = [];
    for (const evt of combined) {
      if (!seen.has(evt.calendar_id)) {
        seen.add(evt.calendar_id);
        deduped.push(evt);
      }
    }
    deduped.sort((a, b) => {
      const dateCompare = a.reading_date.localeCompare(b.reading_date);
      if (dateCompare !== 0) return dateCompare;
      return (a.reading_time || '').localeCompare(b.reading_time || '');
    });
    return deduped;
  }, [data]);

  const hasMore = allEvents.length > maxItems;

  // Group events by date for display
  const displayGroups = React.useMemo(() => {
    const events = showAll ? allEvents : allEvents.slice(0, maxItems);
    const groups: { date: string; label: string; events: CalendarEventSummary[] }[] = [];
    let currentDate = '';
    for (const evt of events) {
      const dateOnly = evt.reading_date.split('T')[0];
      if (dateOnly !== currentDate) {
        currentDate = dateOnly;
        groups.push({ date: dateOnly, label: formatDateHeader(dateOnly), events: [] });
      }
      groups[groups.length - 1].events.push(evt);
    }
    return groups;
  }, [allEvents, showAll, maxItems]);

  return (
    <>
    <ProCard padded>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-1">
                <IonSkeletonText animated style={{ width: '60%', height: '14px' }} />
                <IonSkeletonText animated style={{ width: '80%', height: '16px' }} />
                <IonSkeletonText animated style={{ width: '40%', height: '12px' }} />
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <IonIcon icon={alertCircleOutline} className="text-3xl text-error-500 mb-2" />
            <p className="text-sm text-error-600 dark:text-error-400 mb-2">
              Unable to load calendar events.
            </p>
            <IonButton fill="clear" size="small" onClick={() => refetch()}>
              Try again
            </IonButton>
          </div>
        ) : allEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <IonIcon icon={calendarOutline} className="text-3xl text-text-tertiary dark:text-text-dark-tertiary mb-2 opacity-50" />
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              No upcoming events
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {displayGroups.map((group) => (
              <div key={group.date}>
                <h4 className="text-xs font-semibold text-text-secondary dark:text-text-dark-secondary uppercase tracking-wide mb-2">
                  {group.label}
                </h4>
                <div className="space-y-2">
                  {group.events.map((event) => (
                    <div
                      key={event.calendar_id}
                      className="p-3 rounded-lg bg-background-tertiary dark:bg-background-dark-tertiary border border-border-light dark:border-transparent"
                    >
                      <div className="flex items-start justify-between mb-1">
                        <h5 className="text-sm font-medium text-text-primary dark:text-text-dark-primary flex-1 min-w-0 line-clamp-2">
                          {event.reading_title}
                        </h5>
                        <span className={`text-xs px-2 py-0.5 rounded ml-2 flex-shrink-0 ${getChamberColor(event.chamber)}`}>
                          {event.chamber}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        {(event.reading_time || event.upon_adjournment) && (
                          <span className="flex items-center gap-1">
                            <IonIcon icon={timeOutline} className="text-xs" />
                            {formatTime(event.reading_time, event.upon_adjournment)}
                          </span>
                        )}
                        {event.room_number && (
                          <span className="flex items-center gap-1">
                            <IonIcon icon={locationOutline} className="text-xs" />
                            {event.room_number}
                          </span>
                        )}
                        {event.committee_flag && (
                          <IonBadge color="medium" className="text-xs">Committee</IonBadge>
                        )}
                      </div>
                      {event.bills?.length > 0 && (
                        <p className="text-xs text-primary-600 dark:text-primary-400 font-medium mt-1.5">
                          {event.bills.length} bill{event.bills.length !== 1 ? 's' : ''} on agenda
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {hasMore && (
              <IonButton
                fill="clear"
                size="small"
                onClick={() => setShowAll(!showAll)}
                expand="block"
              >
                <IonIcon icon={showAll ? chevronUpOutline : chevronDownOutline} slot="start" />
                {showAll ? 'Show less' : `Show all ${allEvents.length} events`}
              </IonButton>
            )}
          </div>
        )}
    </ProCard>
    </>
  );
};

export default ProCalendarCard;
